/**
 * API Query Hooks - React Query hooks for common API calls
 * Provides caching and automatic refetching for API data
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getSiteCategories, 
  getSiteContent,
  getProductGroups,
  getEditorsPicksProducts,
  getProductGroupBySlug,
  apiClient
} from '@/lib/api';

// Query keys for cache management
export const queryKeys = {
  categories: ['categories'],
  siteContent: ['siteContent'],
  siteSettings: ['siteSettings'],
  productGroups: (category, region) => ['productGroups', category, region],
  editorsPicks: (region) => ['editorsPicks', region],
  productDetail: (slug) => ['product', slug],
  relatedProducts: (productId, region) => ['relatedProducts', productId, region],
  newsletterStatus: ['newsletterStatus'],
};

/**
 * Hook for fetching site categories
 * Cache time: 10 minutes (categories rarely change)
 */
export const useCategories = () => {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: getSiteCategories,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for fetching site content (homepage, footer, etc.)
 * Cache time: 5 minutes
 */
export const useSiteContent = () => {
  return useQuery({
    queryKey: queryKeys.siteContent,
    queryFn: getSiteContent,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching site settings (ads enabled, regions, etc.)
 * Cache time: 5 minutes
 */
export const useSiteSettings = () => {
  return useQuery({
    queryKey: queryKeys.siteSettings,
    queryFn: async () => {
      const response = await apiClient.get('/site-settings');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching product groups with optional filtering
 * @param {string} category - Category filter (optional)
 * @param {string} region - Region for affiliate links (us/uk)
 * @param {object} options - Additional options like limit
 */
export const useProductGroups = (category, region, options = {}) => {
  const { limit = 8, enabled = true } = options;
  
  return useQuery({
    queryKey: queryKeys.productGroups(category, region),
    queryFn: () => getProductGroups({ category, region, limit }),
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: enabled && !!category,
  });
};

/**
 * Hook for fetching editor's picks
 * @param {string} region - Region for filtering products
 */
export const useEditorsPicks = (region) => {
  return useQuery({
    queryKey: queryKeys.editorsPicks(region),
    queryFn: () => getEditorsPicksProducts(region),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching a single product by slug
 * @param {string} slug - Product slug
 * @param {boolean} enabled - Whether to enable the query
 */
export const useProductDetail = (slug, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.productDetail(slug),
    queryFn: () => getProductGroupBySlug(slug),
    enabled: !!slug && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - normal caching for performance
    retry: false, // Don't retry on 404 errors - immediate redirect
  });
};

/**
 * Hook for prefetching product data (useful for hover prefetch)
 */
export const usePrefetchProduct = () => {
  const queryClient = useQueryClient();
  
  return (slug) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.productDetail(slug),
      queryFn: () => getProductGroupBySlug(slug),
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Hook for fetching related products for a product group
 * Uses the related_products_config from the product to determine filtering
 */
export const useRelatedProducts = (productId, region) => {
  return useQuery({
    queryKey: queryKeys.relatedProducts(productId, region),
    queryFn: async () => {
      if (!productId) return { items: [], filter_type: 'none', tags: [] };
      const params = new URLSearchParams();
      if (region) params.append('region', region);
      params.append('limit', '4');
      const response = await apiClient.get(`/product-groups/${productId}/related?${params}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!productId,
  });
};

/**
 * Hook for invalidating cache when data is mutated
 */
export const useInvalidateCache = () => {
  const queryClient = useQueryClient();
  
  return {
    invalidateCategories: () => queryClient.invalidateQueries({ queryKey: queryKeys.categories }),
    invalidateSiteContent: () => queryClient.invalidateQueries({ queryKey: queryKeys.siteContent }),
    invalidateProductGroups: () => queryClient.invalidateQueries({ queryKey: ['productGroups'] }),
    invalidateEditorsPicks: () => queryClient.invalidateQueries({ queryKey: ['editorsPicks'] }),
    invalidateAll: () => queryClient.invalidateQueries(),
  };
};

/**
 * Hook for fetching newsletter feature status
 * Cache time: 5 minutes
 */
export const useNewsletterStatus = () => {
  return useQuery({
    queryKey: queryKeys.newsletterStatus,
    queryFn: async () => {
      const response = await apiClient.get('/newsletter/status');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};
